import { useSyncExternalStore } from 'react'
import type { Ik, Voortgang } from '../../gedeeld/types'
import {
  haalIk, haalVoortgang, lokaalLezen, lokaalSchrijven, samenvoegen, stuurVoortgang,
  uitloggen as uitloggenBijServer,
} from './api'

/* ===========================================================================
 *  Waar de app zijn stand bewaart
 *
 *  Eén verzameling gegevens voor de hele app, met een handvol luisteraars.
 *  Geen Zustand en geen Redux: er zijn hier vier velden en drie handelingen,
 *  en useSyncExternalStore staat al in React.
 *
 *  Waarom een schrijfactie nooit op een antwoord wacht
 *  ---------------------------------------------------
 *
 *  Wie op "volgende" drukt, hoort de volgende module te zien -- niet een
 *  draaiend rondje terwijl er ergens een verzoek loopt. De stand gaat dus
 *  meteen om, komt meteen in de browseropslag, en gaat daarna pas naar de
 *  server. Mislukt dat, dan staat het er lokaal nog, en gaat het mee bij de
 *  volgende gelegenheid.
 * =========================================================================== */

export interface Staat {
  /** Nog bezig met opstarten? Dan is "niet ingelogd" nog geen conclusie. */
  bezig: boolean
  ik: Ik | null
  voortgang: Voortgang[]
  /**
   * Is de opslag bereikbaar?
   *
   * Staat dit op false, dan zegt het scherm dat de voortgang nog niet
   * bewaard is. Dat hoort de gebruiker te weten: hij heeft net een toets
   * gehaald en mag niet in de veronderstelling verkeren dat het vastligt
   * terwijl het alleen in dit tabblad staat.
   */
  bewaard: boolean
}

let staat: Staat = { bezig: true, ik: null, voortgang: [], bewaard: true }

const luisteraars = new Set<() => void>()

function zet(nieuw: Partial<Staat>): void {
  staat = { ...staat, ...nieuw }
  for (const l of luisteraars) l()
}

function abonneer(l: () => void): () => void {
  luisteraars.add(l)
  return () => { luisteraars.delete(l) }
}

export function useStaat(): Staat {
  return useSyncExternalStore(abonneer, () => staat, () => staat)
}

/* ------------------------------------------------------------------ *
 *  Opstarten
 * ------------------------------------------------------------------ */

let gestart = false

export async function start(): Promise<void> {
  if (gestart) return
  gestart = true

  const ik = await haalIk()
  if (!ik) { zet({ bezig: false, ik: null }); return }

  const lokaal = lokaalLezen(ik.id)
  const vanServer = await haalVoortgang()

  /*
   * Geen antwoord van de server is iets anders dan een leeg antwoord.
   *
   * Bij geen antwoord houden we alleen wat er lokaal staat aan; zou de lege
   * lijst als waarheid gelden, dan lijkt iemands voortgang weg te zijn en
   * begint hij opnieuw -- terwijl er niets kwijt was.
   */
  const samen = vanServer
    ? samenvoegen(vanServer.voortgang, lokaal)
    : lokaal

  lokaalSchrijven(ik.id, samen)
  zet({ bezig: false, ik, voortgang: samen, bewaard: vanServer?.bereikbaar ?? false })

  /* Wat lokaal verder was dan de server, alsnog opsturen. */
  if (vanServer?.bereikbaar) {
    for (const v of samen) {
      const opServer = vanServer.voortgang.find((s) => s.cursus === v.cursus)
      if (!opServer || opServer.bijgewerktAt < v.bijgewerktAt) void duwen(v)
    }
  }
}

/* ------------------------------------------------------------------ *
 *  Wijzigen
 * ------------------------------------------------------------------ */

function huidige(cursus: string, versie: number): Voortgang {
  const er = staat.voortgang.find((v) => v.cursus === cursus)
  /* Een oudere versie telt niet mee: dat was een andere cursus. Zie
     academy_voortgang.versie in migratie 0147. */
  if (er && er.versie === versie) return er
  return {
    cursus, versie, lessen: [], pogingen: 0,
    besteScore: null, geslaagdAt: null, bijgewerktAt: 0,
  }
}

function bewaar(nieuw: Voortgang): void {
  const rest = staat.voortgang.filter((v) => v.cursus !== nieuw.cursus)
  const alles = [...rest, nieuw]
  zet({ voortgang: alles })
  if (staat.ik) lokaalSchrijven(staat.ik.id, alles)
  void duwen(nieuw)
}

async function duwen(v: Voortgang): Promise<void> {
  const uit = await stuurVoortgang(v)
  if (!uit) { zet({ bewaard: false }); return }

  /*
   * Het antwoord van de server overschrijft wat we hadden.
   *
   * Dat is geen formaliteit: de database houdt de hoogste score vast en de
   * eerste slaagdatum (0147). Wie voor de aardigheid nog eens een toets doet
   * en 40 haalt, hoort daarna geen 40 op zijn scherm te zien staan.
   */
  const rest = staat.voortgang.filter((x) => x.cursus !== uit.cursus)
  const alles = [...rest, uit]
  zet({ voortgang: alles, bewaard: true })
  if (staat.ik) lokaalSchrijven(staat.ik.id, alles)
}

/** Een module is gelezen. */
export function moduleAf(cursus: string, versie: number, module: string): void {
  const nu = huidige(cursus, versie)
  if (nu.lessen.includes(module)) return
  bewaar({
    ...nu,
    lessen: [...nu.lessen, module],
    bijgewerktAt: Date.now(),
  })
}

/** De toets is gemaakt. */
export function toetsGedaan(
  cursus: string, versie: number, score: number, geslaagd: boolean,
): void {
  const nu = huidige(cursus, versie)
  bewaar({
    ...nu,
    pogingen: nu.pogingen + 1,
    besteScore: nu.besteScore === null ? score : Math.max(nu.besteScore, score),
    /* De eerste keer dat het lukte, en die datum blijft staan. */
    geslaagdAt: nu.geslaagdAt ?? (geslaagd ? Date.now() : null),
    bijgewerktAt: Date.now(),
  })
}

/**
 * De cursus opnieuw doen.
 *
 * Alleen de gelezen modules gaan weg. Wat blijft staan is de hoogste score,
 * het aantal pogingen en de datum waarop je slaagde -- dat zijn feiten met een
 * datum, en die verdwijnen niet omdat iemand de stof nog eens doorneemt. Zo
 * staat in de lijst van kantoor nog steeds dat je hem gehaald hebt, en op je
 * eigen scherm dat je opnieuw bij module 1 bent.
 *
 * De database doet hier uit zichzelf aan mee: academy_voortgang_zetten() (0147)
 * houdt de score en de slaagdatum vast en neemt de lessenlijst over zoals hij
 * binnenkomt.
 */
export function herbeginnen(cursus: string, versie: number): void {
  const nu = huidige(cursus, versie)
  bewaar({ ...nu, lessen: [], bijgewerktAt: Date.now() })
}

export function voortgangVan(cursus: string, versie: number): Voortgang {
  return huidige(cursus, versie)
}

export async function afmelden(): Promise<void> {
  await uitloggenBijServer()
  gestart = false
  zet({ bezig: false, ik: null, voortgang: [], bewaard: true })
}
